import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input, signal, ViewEncapsulation } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateDirective } from '@ngx-translate/core';

import {
  Breakpoint,
  BreakpointState,
  ButtonComponent,
  FormFooterComponent,
  HrefDirective,
  IncludeComponent,
  MediaQueryService,
  ModalCloseDirective,
  ModalHeaderButtonsComponent,
  ModalHeaderCloseButtonComponent,
  ModalHeaderComponent,
  ModalHeaderTitleComponent,
  ModalNotificationRegistryService,
  ModalNotificationLauncherService,
  ModalNotificationMultipleService,
  PaginationComponent,
  ModalTriggerConfiguration,
  ModalType
} from '@tdwb/core';

@Component({
  imports: [
    NgTemplateOutlet,
    TranslateDirective,
    ModalHeaderComponent,
    ModalHeaderTitleComponent,
    ModalHeaderButtonsComponent,
    ModalHeaderCloseButtonComponent,
    IncludeComponent,
    FormFooterComponent,
    PaginationComponent,
    ButtonComponent,
    HrefDirective,
    ModalCloseDirective
  ],
  selector: 'td-wb-modal-notification-deprecated',
  templateUrl: 'modal-notification-deprecated.component.html',
  styleUrls: ['modal-notification-deprecated.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ModalNotificationDeprecatedComponent {
  readonly triggerId = input.required<string>();

  private readonly welcomeModals = new Set(['welcome_modal_di', 'welcome_modal_fp_pia', 'welcome_modal_pic']);
  private readonly modalNotificationLauncherService = inject(ModalNotificationLauncherService);
  private readonly modalNotificationMultipleService = inject(ModalNotificationMultipleService);
  private readonly modalNotificationInvocationRegistrationService = inject(ModalNotificationRegistryService);
  private readonly mediaQueryService = inject(MediaQueryService);

  protected readonly currentPage = signal(0);
  protected readonly hasWhatsNewModalFeatureFlag = this.modalNotificationMultipleService.hasMultipleWhatsNewModal();
  protected readonly subModals = signal<Array<ModalTriggerConfiguration>>(
    this.hasWhatsNewModalFeatureFlag ? this.modalNotificationMultipleService.getMultipleWhatsNewModal() : []
  );

  protected readonly modalType: ModalType = ModalType.WHATS_NEW;

  private readonly breakpointState = toSignal(this.mediaQueryService.observe(Breakpoint.SM, Breakpoint.MD), {
    initialValue: { matches: false } as BreakpointState
  });

  protected readonly isSmallMediumView = computed(() => this.breakpointState()?.matches ?? false);
  protected readonly activeTriggerId = computed(() => {
    if (this.hasWhatsNewModalFeatureFlag) {
      return this.subModals()[this.currentPage()]?.id;
    }

    return this.triggerId();
  });

  protected readonly triggerContent = computed(() => {
    const triggerId = this.activeTriggerId();

    return triggerId ? this.modalNotificationLauncherService.getTrigger(triggerId) : undefined;
  });

  protected hasContent(): boolean {
    return (this.triggerContent()?.contentURLs?.length ?? 0) > 0;
  }

  protected getCurrentPageLinkId(): string {
    const triggerContent = this.triggerContent();

    if (!triggerContent) {
      return '';
    }

    return this.hasWhatsNewModalFeatureFlag
      ? (triggerContent.contentURLs[0] ?? '')
      : (triggerContent.contentURLs[this.currentPage()] ?? '');
  }

  protected getModalNotificationTitle(): string {
    return this.welcomeModals.has(this.activeTriggerId() ?? '')
      ? 'tdWb.modalNotification.WELCOME_MODAL_TITLE'
      : 'tdWb.modalNotification.NOTIFICATION_MODAL_TITLE';
  }

  protected getIconId(): string {
    return 'modal--info';
  }

  protected isResponsiveView(): boolean {
    // eservices_blended_pic is the only non-responsive modal remaining
    // checking it this way allows responsive to be the new default, which is how it should be
    return this.activeTriggerId() !== 'eservices_blended_pic' && this.isSmallMediumView();
  }

  protected shouldShowPagination(): boolean {
    const triggerContent = this.triggerContent();

    return !!triggerContent && (triggerContent.contentURLs.length > 1 || this.subModals().length > 1);
  }

  protected shouldShowButtons(): boolean {
    const triggerContent = this.triggerContent();

    if (!triggerContent) {
      return false;
    }

    return (
      triggerContent.buttons !== undefined &&
      Array.isArray(triggerContent.contentURLs) &&
      this.currentPage() === triggerContent.contentURLs.length - 1
    ) || (this.hasWhatsNewModalFeatureFlag && this.currentPage() === this.subModals().length - 1);
  }

  protected pageChange(index: number): void {
    this.currentPage.set(index);

    const triggerContent = this.triggerContent();
    if (this.hasWhatsNewModalFeatureFlag && triggerContent &&
      !this.modalNotificationInvocationRegistrationService.checkIfContentHasBeenLaunched(triggerContent)) {
      this.modalNotificationInvocationRegistrationService.registerLaunch(triggerContent);
    }
  }
}
